import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download } from "lucide-react";

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

const isIos = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !/crios|fxios|edgios/i.test(navigator.userAgent);

// Persistent "Install the App" strip pinned to the bottom of every parent
// portal page. There's no dismiss button on purpose — the disclaimer stays
// visible until the user actually installs the app (standalone mode).
export const PortalInstallGate = () => {
  const { pathname } = useLocation();
  const inPortal = pathname.startsWith("/portal");
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (!inPortal) return;
    const onBip = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [inPortal]);

  // Push body content up so the banner never obscures portal buttons/inputs.
  useEffect(() => {
    if (!inPortal || installed) return;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = "88px";
    return () => { document.body.style.paddingBottom = prev; };
  }, [inPortal, installed]);

  if (!inPortal || installed) return null;

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const message = isIos()
    ? "For the best experience, install Zavi FC. Tap the Share icon in Safari, then Add to Home Screen."
    : deferred
      ? "For the best experience, install the Zavi FC app on your device."
      : "For the best experience, install Zavi FC from your browser menu (Add to Home Screen).";

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[65] bg-[#0A0A0A]/98 backdrop-blur-md border-t border-[#D4AF37]/60 shadow-[0_-12px_40px_rgba(0,0,0,0.7)]"
      role="region"
      aria-label="Install the Zavi FC app"
      data-testid="portal-install-gate"
    >
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
        <img src="/pwa-192.png" alt="" className="w-10 h-10 border border-[#D4AF37]/40 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37] font-semibold">
            Install The App
          </p>
          <p className="text-white/75 text-xs mt-0.5 leading-snug">{message}</p>
        </div>
        {!isIos() && deferred && (
          <button
            onClick={install}
            className="bg-[#D4AF37] hover:bg-[#F3D059] text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2.5 flex items-center gap-1.5 shrink-0"
            data-testid="portal-install-cta"
          >
            <Download size={11} /> Install
          </button>
        )}
      </div>
    </div>
  );
};
